import {
  actionPlanV2Api,
  type AiDeleteTaskResponse,
  type AiGetTaskResponse,
  type AiListCongViecResponse,
} from "@/services/action-plan-v2";
import { unwrapApiResponse } from "@/components/common/ActionPlanV2/actionPlanAiConfirm";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import type { ActionPlanAiPendingPlan } from "@/types/action-plan-v2/ActionPlanAiPlan";
import type {
  ActionPlanAiAmbiguousMatch,
  ActionPlanAiTaskListPayload,
  ActionPlanAiTaskPickAction,
  ActionPlanAiViewParams,
} from "@/types/action-plan-v2/ActionPlanAiTaskList";

function pickField(obj: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (key in obj) return obj[key];
    const alt = key.charAt(0).toLowerCase() + key.slice(1);
    if (alt in obj) return obj[alt];
  }
  return undefined;
}

function mapListPayload(data: Record<string, unknown>, base: ActionPlanAiTaskListPayload): ActionPlanAiTaskListPayload {
  const rawItems = (pickField(data, "items", "Items") as Record<string, unknown>[] | undefined) ?? [];
  return {
    ...base,
    page: Number(pickField(data, "page", "Page") ?? base.page),
    pageSize: Number(pickField(data, "pageSize", "PageSize") ?? base.pageSize),
    total: Number(pickField(data, "total", "Total") ?? 0),
    totalPages: Number(pickField(data, "totalPages", "TotalPages") ?? 0),
    query: String(pickField(data, "query", "Query") ?? base.query ?? ""),
    intent: String(pickField(data, "intent", "Intent") ?? base.intent ?? "browse"),
    items: rawItems
      .map((t) => ({
        id: String(pickField(t, "id", "Id") ?? ""),
        tenCongViec:
          pickField(t, "tenCongViec", "TenCongViec") != null
            ? String(pickField(t, "tenCongViec", "TenCongViec"))
            : undefined,
        ngayKetThuc:
          pickField(t, "ngayKetThuc", "NgayKetThuc") != null
            ? String(pickField(t, "ngayKetThuc", "NgayKetThuc"))
            : undefined,
        ngayBatDau:
          pickField(t, "ngayBatDau", "NgayBatDau") != null
            ? String(pickField(t, "ngayBatDau", "NgayBatDau"))
            : undefined,
        hasChild: Boolean(pickField(t, "hasChild", "HasChild")),
        level:
          pickField(t, "level", "Level") != null ? Number(pickField(t, "level", "Level")) : undefined,
        bucket:
          pickField(t, "bucket", "Bucket") != null ? String(pickField(t, "bucket", "Bucket")) : undefined,
        tenNguoiPhuTrach:
          pickField(t, "tenNguoiPhuTrach", "TenNguoiPhuTrach") != null
            ? String(pickField(t, "tenNguoiPhuTrach", "TenNguoiPhuTrach"))
            : undefined,
        tenNguoiTao:
          pickField(t, "tenNguoiTao", "TenNguoiTao") != null
            ? String(pickField(t, "tenNguoiTao", "TenNguoiTao"))
            : undefined,
        isMine: pickField(t, "isMine", "IsMine") != null ? Boolean(pickField(t, "isMine", "IsMine")) : undefined,
        canEdit: pickField(t, "canEdit", "CanEdit") != null ? Boolean(pickField(t, "canEdit", "CanEdit")) : undefined,
        isDuAn: Boolean(pickField(t, "isDuAn", "IsDuAn")),
      }))
      .filter((t) => t.id),
    ...mapViewParts(data, base),
  };
}

function mapViewParts(
  data: Record<string, unknown>,
  base: ActionPlanAiTaskListPayload,
): Pick<ActionPlanAiTaskListPayload, "view" | "groups" | "ambiguous"> {
  const rawGroups = (pickField(data, "nhomTheoNguoiPhuTrach", "NhomTheoNguoiPhuTrach") as Record<string, unknown>[] | undefined) ?? [];
  const rawAmbiguous = (pickField(data, "ambiguousMatches", "AmbiguousMatches") as Record<string, unknown>[] | undefined) ?? [];
  const viewTen = (key: string) => {
    const v = pickField(data, key, key.charAt(0).toUpperCase() + key.slice(1));
    return v != null && String(v) ? String(v) : undefined;
  };
  const view = base.view
    ? {
        ...base.view,
        tenNhanVien: viewTen("viewTenNhanVien") ?? base.view.tenNhanVien,
        tenNguoiTao: viewTen("viewTenNguoiTao") ?? base.view.tenNguoiTao,
        tenBoPhan: viewTen("viewTenBoPhan") ?? base.view.tenBoPhan,
      }
    : base.view;
  return {
    view,
    groups: rawGroups
      .map((g) => ({
        id: String(pickField(g, "nguoiPhuTrachId", "NguoiPhuTrachId") ?? ""),
        ten: pickField(g, "ten", "Ten") != null ? String(pickField(g, "ten", "Ten")) : undefined,
        soViec: Number(pickField(g, "soViec", "SoViec") ?? 0),
        treHan: Number(pickField(g, "treHan", "TreHan") ?? 0),
      }))
      .filter((g) => g.id),
    ambiguous: rawAmbiguous.map((m) => ({
      id: String(pickField(m, "id", "Id") ?? ""),
      ten: String(pickField(m, "ten", "Ten") ?? ""),
      boPhan: pickField(m, "boPhan", "BoPhan") != null ? String(pickField(m, "boPhan", "BoPhan")) : undefined,
      loai: pickField(m, "loai", "Loai") != null ? String(pickField(m, "loai", "Loai")) : undefined,
    })),
  };
}

/** Tham số xem việc người khác sau khi user chọn một ứng viên trùng tên. */
export function viewParamsAfterPick(
  params: ActionPlanAiViewParams,
  match: ActionPlanAiAmbiguousMatch,
): ActionPlanAiViewParams {
  const next = { ...params };
  if (match.loai === "nguoi_tao") {
    next.nguoiTaoId = match.id;
    delete next.tenNguoiTao;
  } else if (match.loai === "phong_ban") {
    next.boPhanId = match.id;
    delete next.tenBoPhan;
  } else {
    next.nguoiPhuTrachId = match.id;
    delete next.tenNhanVien;
  }
  return next;
}

function mapGetTask(data: Record<string, unknown>) {
  return {
    tenCongViec:
      pickField(data, "tenCongViec", "TenCongViec") != null
        ? String(pickField(data, "tenCongViec", "TenCongViec"))
        : undefined,
    moTa: pickField(data, "moTa", "MoTa") != null ? String(pickField(data, "moTa", "MoTa")) : undefined,
    ngayBatDau:
      pickField(data, "ngayBatDau", "NgayBatDau") != null
        ? String(pickField(data, "ngayBatDau", "NgayBatDau"))
        : undefined,
    ngayKetThuc:
      pickField(data, "ngayKetThuc", "NgayKetThuc") != null
        ? String(pickField(data, "ngayKetThuc", "NgayKetThuc"))
        : undefined,
    mucTieuId:
      pickField(data, "mucTieuId", "MucTieuId") != null
        ? String(pickField(data, "mucTieuId", "MucTieuId"))
        : undefined,
    nguoiPhuTrachId:
      pickField(data, "nguoiPhuTrachId", "NguoiPhuTrachId") != null
        ? String(pickField(data, "nguoiPhuTrachId", "NguoiPhuTrachId"))
        : undefined,
  };
}

export async function fetchActionPlanAiTaskListPage(
  list: ActionPlanAiTaskListPayload,
  page: number,
  query?: string,
  viewParams?: ActionPlanAiViewParams,
): Promise<ActionPlanAiTaskListPayload> {
  const params = viewParams ?? list.view?.params;
  const raw = await actionPlanV2Api.aiListCongViec({
    idChoice: String(list.idChoice || "").trim(),
    year: list.year ?? new Date().getFullYear(),
    phanHe: list.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
    page,
    pageSize: list.pageSize || 8,
    query: query ?? list.query,
    intent: list.intent,
    ...params,
  });
  const res = unwrapApiResponse<AiListCongViecResponse>(raw);
  if (!res?.success || res.data == null) {
    throw new Error(res?.message || "Không tải được danh sách việc.");
  }
  return mapListPayload(res.data as unknown as Record<string, unknown>, {
    ...list,
    page,
    query: query ?? list.query,
    view: list.view && params ? { ...list.view, params } : list.view,
  });
}

export async function buildActionPlanPlanFromTaskPick(
  list: ActionPlanAiTaskListPayload,
  taskId: string,
  action: ActionPlanAiTaskPickAction,
): Promise<ActionPlanAiPendingPlan> {
  const scope = {
    idChoice: String(list.idChoice || "").trim(),
    year: list.year ?? new Date().getFullYear(),
    phanHe: list.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
  };
  if (action === "create_child") {
    const row = list.items.find((i) => i.id === taskId);
    return {
      action: "create",
      draftForm: true,
      mustManual: false,
      idChoice: scope.idChoice,
      year: scope.year,
      phanHe: scope.phanHe,
      parentId: taskId,
      parentTen: row?.tenCongViec,
      tenCongViec: "",
      mucTieuId: "",
      ngayKetThuc: "",
      nguoiPhuTrachId: "",
    };
  }
  if (action === "delete") {
    // Xem trước (server kiểm tra người tạo + phụ trách, đếm việc con) — chưa xoá.
    const rawDel = await actionPlanV2Api.aiDeleteTask({ taskId, ...scope, confirm: false });
    const del = unwrapApiResponse<AiDeleteTaskResponse>(rawDel);
    if (!del?.success || del.data == null) {
      throw new Error(del?.message || "Không xoá được công việc này.");
    }
    return {
      action: "delete",
      mustManual: false,
      taskId,
      idChoice: scope.idChoice,
      year: scope.year,
      phanHe: scope.phanHe,
      tenCongViec: del.data.tenCongViec ?? list.items.find((i) => i.id === taskId)?.tenCongViec,
      soViecCon: del.data.soViecCon ?? 0,
      coTienDo: Boolean(del.data.coTienDo),
    };
  }
  const raw = await actionPlanV2Api.aiGetTask({
    taskId,
    idChoice: scope.idChoice,
    year: scope.year,
    phanHe: scope.phanHe,
  });
  const res = unwrapApiResponse<AiGetTaskResponse>(raw);
  if (!res?.success || res.data == null) {
    throw new Error(res?.message || "Không tải được chi tiết việc.");
  }
  const t = mapGetTask(res.data as unknown as Record<string, unknown>);
  return {
    action: "update",
    draftForm: true,
    mustManual: false,
    taskId,
    idChoice: scope.idChoice,
    year: scope.year,
    phanHe: scope.phanHe,
    tenCongViec: t.tenCongViec,
    moTa: t.moTa,
    ngayBatDau: t.ngayBatDau,
    ngayKetThuc: t.ngayKetThuc,
    mucTieuId: t.mucTieuId,
    nguoiPhuTrachId: t.nguoiPhuTrachId,
  };
}

export function bucketLabel(bucket?: string): string {
  switch (bucket) {
    case "overdue":
      return "Quá hạn";
    case "today":
      return "Hôm nay";
    case "upcoming":
      return "Sắp tới";
    case "in_progress":
      return "Đang làm";
    case "root":
      return "Việc gốc";
    default:
      return "";
  }
}
